import { Injectable } from '@angular/core';

const STORAGE_KEY = 'sso_scope';

export interface SsoScope {
  entryUrl: string;
  mainLabUrl: string | null;
  issuedAt: number;
}

/**
 * Sticky, per-tab marker for an SSO session. Source of truth for
 * `isSSO` / `mainLabUrl` on the FE — independent of the JWT's claims so
 * refresh-token rotation (which may drop the claims) doesn't break the
 * kiosk UI or scope guard.
 *
 * Lives in sessionStorage so:
 *   - it auto-clears on tab close
 *   - it doesn't leak across tabs
 *   - the `storage` cross-tab listener in AppComponent (which watches
 *     localStorage only) doesn't interact with it
 */
@Injectable({ providedIn: 'root' })
export class SsoScopeService {
  set(scope: { entryUrl: string; mainLabUrl?: string | null }): void {
    const existing = this.get();
    const next: SsoScope = {
      entryUrl: scope.entryUrl,
      mainLabUrl: scope.mainLabUrl ?? existing?.mainLabUrl ?? null,
      issuedAt: existing?.issuedAt ?? Date.now(),
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      console.error('[SsoScope] failed to write sessionStorage', e);
    }
  }

  setMainLabUrl(url: string | null): void {
    const existing = this.get();
    if (!existing) return;
    this.set({ entryUrl: existing.entryUrl, mainLabUrl: url });
  }

  get(): SsoScope | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as SsoScope;
    } catch {
      return null;
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // best effort
    }
  }

  isActive(): boolean {
    return this.get() !== null;
  }
}
